import React, {useEffect, useState, useCallback} from 'react';
import {useLanguagesContext} from '../../contexts/LanguagesContext';
import {useUserStatisticsContext} from '../../contexts/UserStatisticsContext';
import useGoals from '../../hooks/useGoals';
import {motion} from 'framer-motion';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSpinner} from '@fortawesome/pro-solid-svg-icons';
import {faLoader} from '@fortawesome/pro-solid-svg-icons'; 
import {faArrowDown} from '@fortawesome/pro-solid-svg-icons';
import {faArrowUp} from '@fortawesome/pro-solid-svg-icons';
import Button from '../../components/general/Button';
import NotificationMessage from '../../components/general/NotificationMessage';
import NoActiveTargetLanguage from '../../components/errors/NoActiveTargetLanguage';

const MyDailyGoals = () => {
    const {activeUserLanguages, allLanguages, globalSelectedTargetLanguage} = useLanguagesContext();
    const { 
        saveDailyGoalsInTargetLanguageApi,
        getCurrentUserGoals, 
        getCurrentUserGoalsInTargetLanguage} = useGoals();
    const { 
        refreshUserGoalsFunction} = useUserStatisticsContext();
    const [days, setDays] = useState(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']);
    const [showIndividualDays, setShowIndividualDays] = useState(true);
    const [itemsPerDay, setItemsPerDay] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showNotificationMessage, setShowNotificationMessage] = useState(false);
    const [notificationStatus, setNotificationStatus] = useState("");
    const [dailyGoals, setDailyGoals] = useState(null);

    const [dailyGoalsLoading, setDailyGoalsLoading] = useState(true);

    const getCurrentUserGoalsCallback = useCallback(async ()=>{
        if (globalSelectedTargetLanguage === null){return null}
        if (!dailyGoalsLoading) {return null};
        let r = await getCurrentUserGoalsInTargetLanguage(globalSelectedTargetLanguage);
        if (r !== false && r !== null){
            setDailyGoals(r);
        } else {
            setDailyGoals({'total_correct_items_per_day': Array(7).fill(0)})
        }
        setDailyGoalsLoading(false);
    },[dailyGoalsLoading, getCurrentUserGoalsInTargetLanguage, globalSelectedTargetLanguage]);

    useEffect(()=>{
        const fetchAsyncData = async () => {
            await getCurrentUserGoalsCallback();
        } 

        if (dailyGoalsLoading){
            fetchAsyncData();
        }
        
        return () => {
        }
    }, [dailyGoalsLoading, getCurrentUserGoalsCallback]);

    useEffect(()=>{
        setDailyGoalsLoading(true);
    },[globalSelectedTargetLanguage])

    useEffect(()=>{
        if (globalSelectedTargetLanguage !== null && dailyGoals !== null && dailyGoals !== false && !dailyGoalsLoading){
            let obj = null;
            let found = false;
            if ('total_correct_items_per_day' in dailyGoals){
                found = true;
                obj = [dailyGoals['total_correct_items_per_day'][0], ...dailyGoals['total_correct_items_per_day']]; 
            }
            
            if (!found){
                obj = Array(8).fill(0);
            }     
            
            setItemsPerDay(obj);
        }
    },[globalSelectedTargetLanguage, dailyGoals, dailyGoalsLoading]);

    const changeGlobalItemsPerDay = (e) => {
        let o = JSON.parse(JSON.stringify(itemsPerDay));
        o = Array(8).fill(parseInt(e.target.value));
        setItemsPerDay(o);
    }

    const changeIndividualDayTarget = (e, index) => {
        let o = JSON.parse(JSON.stringify(itemsPerDay));
        o[index+1] = parseInt(e.target.value);
        setItemsPerDay(o);
    }

    const saveGoals = async () => {
        let o = JSON.parse(JSON.stringify(itemsPerDay));
        o.shift();
        setSaving(true);
        let success = await saveDailyGoalsInTargetLanguageApi(o, globalSelectedTargetLanguage);
        setSaving(false);
        if (success){
            setNotificationStatus("success");
            setShowNotificationMessage(true);
            refreshUserGoalsFunction();
        } else {
            setNotificationStatus("error");
            setShowNotificationMessage(true);
        }
    }

    /*
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-1 w-full m-auto">
            {activeUserLanguages !== null && activeUserLanguages.map((lang, index)=>(
                <>
                    <motion.div onClick={()=>setSelectedLang(lang['target_language'])} whileHover={{scale:1.03}} className={(selectedLang === lang.target_language ? 'bg-charcoal text-white ' : 'bg-gray-100 ') +  "  cursor-pointer font-bold rounded-lg p-2"} key={"lang_"+index}>
                        {lang.name.en}
                    </motion.div>
                </>
            ))}
        </div>
    */

    return (
    <>
{(activeUserLanguages !== null && activeUserLanguages.length === 0) && (
    <>
        <NoActiveTargetLanguage />
    </>
)}
{(activeUserLanguages !== null && activeUserLanguages.length > 0) && (
    <div className="relative">
        {saving && (
            <>
                <div className="text-center z-10 text-4xl absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 text-black dark:text-gray-300">
                    <FontAwesomeIcon icon={faLoader} className="fa-spin" />
                </div>
            </>
        )}

        <div className={(saving ? 'opacity-20 ' : '') + "max-w-2xl mx-auto p-6"}>
            {/* Header Section */}
            <div className="text-center mb-12">
                <h1 className="">
                    Daily Goals for                     {(allLanguages !== null && globalSelectedTargetLanguage !== null) && allLanguages[0][globalSelectedTargetLanguage]['name']['en']}
                </h1>

                {activeUserLanguages.length > 1 && (
                    <div className="mt-3 text-sm text-gray-500 dark:text-gray-500">
                        Change the language in the menu to set goals for another language
                    </div>
                )}
            </div>

            {dailyGoalsLoading && (
                <div className="flex justify-center items-center py-12">
                    <div className="text-center">
                        <FontAwesomeIcon icon={faSpinner} className="fa-pulse text-4xl text-gray-400 dark:text-gray-500 mb-4" />
                        <p className="text-gray-600 dark:text-gray-400">Loading your goals...</p>
                    </div>
                </div>
            )}

            {!dailyGoalsLoading && itemsPerDay !== null && (
                <div className="space-y-8">
                    {/* Global Goals Section */}
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-5"
                    >
                        <div className="mb-5">
                            <h2 className="">
                                Daily Target
                            </h2>
                            <p className="text-gray-600 dark:text-gray-400 text-center text-sm">
                                Set your daily learning target for all days of the week
                            </p>
                        </div>
                        
                        <div className="max-w-md">
                            <label className="block text-sm font-medium text-charcoal dark:text-gray-300 mb-3">
                                Correct exercises per day
                            </label>
                            <input
                                value={itemsPerDay[0]}
                                onChange={(e) => changeGlobalItemsPerDay(e)}
                                type="number"
                                min="0"
                                className="w-full bg-gray-50 dark:bg-gray-700 border-0 border-b-2 border-charcoal dark:border-gray-600 rounded-none py-4 px-0 text-2xl font-light text-charcoal dark:text-gray-100 focus:outline-none focus:border-persian-green dark:focus:border-persian-green transition-all duration-200"
                            />
                        </div>
                    </motion.div>

                    {/* Individual Days Section */}
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-5"
                    >
                        <div 
                            onClick={() => setShowIndividualDays(!showIndividualDays)}
                            className="cursor-pointer group mb-5"
                        >
                            <div className="flex items-center justify-between">
                                <div className="w-full">
                                    <h2 className="">
                                        Weekly Schedule
                                    </h2>
                                    <p className="text-gray-600 text-center dark:text-gray-400 text-sm">
                                        Customize goals for specific days
                                    </p>
                                </div>
                                <div className="text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
                                    <FontAwesomeIcon 
                                        icon={showIndividualDays ? faArrowUp : faArrowDown} 
                                        className="text-lg"
                                    />
                                </div>
                            </div>
                        </div>

                        {showIndividualDays && (
                            <motion.div 
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                className="pt-5 border-t border-gray-200 dark:border-gray-600"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {days.map((day, index) => (
                                        <div key={'day_' + index} className="space-y-3">
                                            <label className="block text-sm font-medium text-charcoal dark:text-gray-300">
                                                {day}
                                            </label>
                                            <input
                                                onChange={(e) => changeIndividualDayTarget(e, index)}
                                                value={itemsPerDay[index + 1]}
                                                type="number"
                                                min="0"
                                                className="w-full bg-gray-50 dark:bg-gray-700 border-0 border-b-2 border-charcoal dark:border-gray-600 rounded-none py-3 px-0 text-lg font-light text-charcoal dark:text-gray-100 focus:outline-none focus:border-persian-green dark:focus:border-persian-green transition-all duration-200"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        )}
                    </motion.div>

                    {/* Save Button */}
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="flex justify-center pt-6"
                    >
                        <Button 
                            onClick={saveGoals}
                            text="Save Goals"
                            color="green"
                            size="normal"
                        />
                    </motion.div>
                </div>
            )}
        </div>
        
        <NotificationMessage
            notificationStatus={notificationStatus}
            showMessageBool={showNotificationMessage}
            setShowMessageBool={setShowNotificationMessage}
        />
    </div>
)}
</>

    )
}

export default MyDailyGoals